
import React from 'react'
import { Poller } from 'widgets-shared/services'
import { compose } from 'widgets-shared/core'
import { composeWidget, createWidgetInitFunction } from 'widgets-shared/core'
import { withMuiThemeProvider } from 'smartstream-widgets/enhancers/withMuiThemeProvider'
import { withHotspotTracking } from 'smartstream-widgets/enhancers/withHotspotTracking'
import { withWidthBreakpoints } from 'smartstream-widgets/enhancers/withWidthBreakpoints'
import { dispatchWidgetDataChanged } from 'smartstream-widgets/utils/widgetDataChanged'
import OddsComparisonGridComponent from './OddsComparisonGridComponent'
import { safelyGetNestedValue } from 'widgets-shared/utilities'
import { motorSportsIds } from 'smartstream-widgets/constants/competitions'

import { WidgetInitializationParameters } from 'widgets-shared/core'
import { Props, WidgetData, State } from 'smartstream-widgets/widgets/OddsComparisonGridV2/types'
import { MarketType } from 'smartstream-widgets/types/market'
import { PollerQueryParameter, PollerType } from "smartstream-widgets/types/sharedModule/core"

export class OddsComparisonGridV2 extends React.Component<Props, State> {
  poller: PollerType
  baseQueryParams: {
    [K in string]: string
  }
  isMotorSport: boolean

  constructor (props: Props) {
    super(props)
    const {
      config: { 
        POLL_URL,
         POLLING_FREQUENCY,
         initialMarketType = 'moneyLine',
         isBookmakerLinkEnabled
        },
      data,
      renderParameters: { productName, region, country, themeMode },
      routingKey
    } = props

    this.state = {
      widgetData: data || {},
      activeTab: safelyGetNestedValue(data, ['available', '0', 'competitionId'], null) as string | null,
      dataRequestAttempts: 0,
      isLoading: false,
      lastUpdateAction: 'render',
      page: '1',
      nextPage: '',
      isBookmakerLinkEnabled: isBookmakerLinkEnabled
    }

    this.tabChange = this.tabChange.bind(this) as this['tabChange']
    this.marketTypeChange = this.marketTypeChange.bind(this) as this['marketTypeChange']
    this.onPoll = this.onPoll.bind(this) as this['onPoll']
    this.onError = this.onError.bind(this) as this['onError']
    this.paginationChange = this.paginationChange.bind(this) as this['paginationChange']
    this.isMotorSport = motorSportsIds.includes(this.state.activeTab)

    const initialMarkets = safelyGetNestedValue(data, ['available', '0', 'marketTypes'], []) as Array<string>
    this.baseQueryParams = {
      productName,
      page: '1',
      region,
      country,
      themeMode,
      marketType: initialMarkets.includes(initialMarketType) ? initialMarketType : initialMarkets[0],
      isMobile: 'false'
    }
    const pollerOptions = {
      url: POLL_URL,
      frequency: POLLING_FREQUENCY,
      queryParams: this.baseQueryParams,
      onError: this.onError,
      onPoll: this.onPoll,
      routingKey: routingKey
    }
    this.poller = new Poller(pollerOptions)
  }

  onPoll = (responseData: WidgetData): void => {
    const hasWidgetData = responseData && Array.isArray(responseData.available) && responseData.available.length > 0

    if (hasWidgetData) {
      this.setState(state => {
        const actualActiveIsPresent = responseData.available.some(cp => cp.competitionId === state.activeTab)
        const newState = {
          widgetData: responseData,
          activeTab: actualActiveIsPresent ? state.activeTab : responseData.available[0].competitionId,
          dataRequestAttempts: 0,
          isLoading: false,
          page: state.nextPage || state.page,
          nextPage: ''
        }
        return newState
      })
    } else {
      this.onFailedAttempt()
    }
  }

  onError = (error: Error): void => {
    const { logger } = this.props
    this.onFailedAttempt()
    logger.error.bind(logger)
    logger.error(error.toString())
  }

  onFailedAttempt = () => {
    this.setState(state => {
      const actualAttempt = state.dataRequestAttempts + 1
      if (actualAttempt >= 2) {
        this.poller.stopPolling()
        return { dataRequestAttempts: 0, isLoading: false, widgetData: {} }
      }
      return { dataRequestAttempts: actualAttempt }
    })
  }

  componentDidMount () {
    const { messageBus } = this.props
    const { activeTab } = this.state
    if (messageBus && activeTab) {
      dispatchWidgetDataChanged(messageBus,{ competitionId: activeTab })
    }
  }

  componentDidUpdate (prevProps: Props, prevState: State) {
    const { messageBus, breakpointSize } = this.props
    const { activeTab } = this.state
    if (messageBus && activeTab && activeTab !== prevState.activeTab) {
      dispatchWidgetDataChanged(messageBus,{ competitionId: activeTab })
    }
    const breakpointIsValid = (breakpointSize === 'xs' || prevProps.breakpointSize === 'xs') && (breakpointSize !== prevProps.breakpointSize)
    if (breakpointIsValid && !this.isMotorSport) {
      this.updatePollerByBreakpoint(breakpointSize)
    }
  }

  getMobileViewParam (breakpoint: string): string {
    const mobileBreakpoints = ['xs', 'cs']
    const isMobileView = mobileBreakpoints.includes(breakpoint)

    return isMobileView ? 'true' : 'false'
  }

  updatePollerByBreakpoint = (breakpoint: string) => {
    const isMobileView = breakpoint === 'xs'
    const isMobileParam = this.getMobileViewParam(breakpoint)
    const newMarketType = isMobileView ? 'all' : this.baseQueryParams.marketType
    this.setState({ isLoading: true })
    this.updatePollerOptions({
      marketType: newMarketType,
      isMobile: isMobileParam
    })
  }

  componentWillUnmount () {
    this.poller.stopPolling()
  }

  updatePollerOptions = (newValues: {
    [K in string]: string
  }) => {
    const mixValues = typeof newValues === 'object' && Object.assign({}, this.poller.options.queryParams, newValues) as PollerQueryParameter
    mixValues && this.poller.changeOptions({
      queryParams: mixValues
    })
  }

  tabChange = (tabId: string): void => {
    if (!this.state.isLoading) {
      const { config: { initialMarketType = 'moneyLine' }, breakpointSize } = this.props
      const { widgetData: { available = [] } } = this.state
      const availableItem = available.find(cp => cp.competitionId === tabId)
      const marketTypeGroup = availableItem && availableItem.marketTypes || []
      const isMobileParam = this.getMobileViewParam(breakpointSize)
      let relevantMarketType = marketTypeGroup.includes(initialMarketType) ? initialMarketType : marketTypeGroup[0]
      if (breakpointSize === 'xs') relevantMarketType = 'all'

      this.isMotorSport = motorSportsIds.includes(tabId)
      this.setState({ activeTab: tabId, isLoading: true, lastUpdateAction: 'competitionChange', page: '1' })
      this.updatePollerOptions(
        {
          ...this.baseQueryParams,
          competitionId: tabId,
          marketType: relevantMarketType,
          isMobile: isMobileParam
        })
    }
  }

  marketTypeChange = (marketType: MarketType): void => {
    const { breakpointSize } = this.props
    const isMobileParam = this.getMobileViewParam(breakpointSize)
    this.updatePollerOptions({ ...this.baseQueryParams, marketType, isMobile: isMobileParam })
  }

  paginationChange = (requestPage: string): void => {
    const { breakpointSize } = this.props
    const isMobileParam = this.getMobileViewParam(breakpointSize)
    this.setState({ lastUpdateAction: 'pageChange', nextPage: requestPage})
    this.updatePollerOptions({ page: requestPage, isMobile: isMobileParam  })
  }

  render () {
    const { breakpointSize } = this.props
    const { widgetData: { available = [], data = {}, disclaimer = '', footerLink, bookmakers }, activeTab, isLoading, page, lastUpdateAction, isBookmakerLinkEnabled } = this.state
    const isMobileView = breakpointSize === 'xs'

    return (
      <OddsComparisonGridComponent
        bookmakers={bookmakers}
        disclaimer={disclaimer}
        footerLink ={footerLink}
        isMotorSport={this.isMotorSport}
        mobileView={isMobileView}
        availableCompetitions={available}
        activeTab={activeTab}
        activePage={page}
        lastUpdateAction={lastUpdateAction}
        tabChange={this.tabChange}
        data={data}
        isLoading={isLoading}
        marketTypeHandler={this.marketTypeChange}
        paginationHandler={this.paginationChange}
        isBookmakerLinkEnabled={isBookmakerLinkEnabled}
      />
    )
  }
}

const OddsComparisonGridEnhanced = compose(
  withMuiThemeProvider,
  withHotspotTracking,
  withWidthBreakpoints
)(OddsComparisonGridV2) as React.Component

export default (widgetInitializationParameters: WidgetInitializationParameters) => {
  return composeWidget(OddsComparisonGridEnhanced, widgetInitializationParameters) as React.ElementType
}

createWidgetInitFunction(OddsComparisonGridEnhanced, 'OddsComparisonGridV2')
